import { useField } from 'formik'
import Styles from './GeneratedDesignPreview.module.scss'
import { useEffect, useMemo, useRef } from 'react'
import { useGenerateDesignContentSync } from 'hooks/queries/workspaces/useGenerateDesignContentSync'
import { LoadingShimmerer } from 'components/LoadingShimmerer/LoadingShimmerer'
import { DesignContentPreview } from 'components/DesignContentPreview/DesignContentPreview'
import useButtonWithScrollCancel from 'hooks/useButtonWithScrollCancel'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'

interface Props {
  templateId: number
  aspectRatio: number
  onPress: (content: any) => void
  borderRadius?: number | string
}

const GeneratedDesignPreview = observer(({ templateId, aspectRatio, onPress, borderRadius }: Props) => {
  const { visualElements } = useRootStore()
  const [{ value: topic }] = useField('topic')
  const [{ value: language }] = useField('language')
  const [{ value: brand_voice_id }] = useField('brand_voice_id')
  const [{ value: tone }] = useField('tone')
  const [{ value: brand_kit_id }] = useField('brand_kit_id')
  const [{ value: brand_visual_kit_id }] = useField('brand_visual_kit_id')
  const [{ value: initial_media_file_id }] = useField('initial_media_file_id')
  const [{ value: initial_media_file_remove_background }] = useField('initial_media_file_remove_background')
  const [, , { setValue: setContent }] = useField('content')
  const generateMutation = useGenerateDesignContentSync({ retry: 3 })
  const toneDep = JSON.stringify(tone)
  const request = generateMutation.data
  const generatedContent = request?.generated_content
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!templateId) {
      return
    }
    const controller = new AbortController()
    const template = visualElements.getElement(templateId)
    generateMutation.mutate({
      source_visual_element_id: templateId,
      topic,
      language,
      brand_voice_id,
      tone,
      brand_kit_id,
      brand_visual_kit_id,
      initial_media_file_id,
      initial_media_file_remove_background,
      apply_brand: template?.basic_layout,
      signal: controller.signal,
    })

    return () => {
      controller.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    templateId,
    topic,
    language,
    brand_voice_id,
    toneDep,
    brand_kit_id,
    initial_media_file_id,
    initial_media_file_remove_background,
  ])

  const content = useMemo(() => {
    if (generatedContent) {
      return JSON.parse(generatedContent)
    }
  }, [generatedContent])

  const handlePress = () => {
    if (generateMutation.isLoading) {
      return
    }

    setContent(content)
    onPress(content)
  }

  const { buttonProps } = useButtonWithScrollCancel({ onPress: handlePress, elementType: 'div' }, ref)

  return (
    <div {...buttonProps} style={{ aspectRatio }} ref={ref}>
      <LoadingShimmerer
        className={Styles.designPreview}
        show={generateMutation.isLoading || !templateId}
        borderRadius={borderRadius}
      >
        {content && (
          <div
            className={Styles.designPreview}
            style={{ borderRadius }}
            data-testid={`design-content-preview-${templateId}`}
          >
            <DesignContentPreview content={content} />
          </div>
        )}
      </LoadingShimmerer>
    </div>
  )
})

export { GeneratedDesignPreview }
